import React, { useState } from "react";
import BaseTextField from "../../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";
import {
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../../store/workOrder/workOrderSlice";
import BaseCheckbox from "../../../../components/ui/BaseCheckbox";
import { CurrencySymbol } from "../../../../utils/enum";

const HourlyPay = ({ disabled = false }) => {
  const dispatch = useDispatch();
  const payment = useSelector((state) => state.workOrder.payment.pay);
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);

  const [isMinimumHourError, setIsMinimumHourError] = useState(false);

  // An empty box and a zero/negative one are different mistakes. CurrencySymbol
  // maps to the currency code ("EUR"), not a glyph, so it needs a space.
  const additionalHourlyRateError = (value) => {
    if (value === "" || value === null || value === undefined) {
      return "Additional Hourly Rate is required";
    }

    const currency = CurrencySymbol[payment.currency];
    return currency
      ? `Minimum Additional Hourly Rate is ${currency} 1`
      : "Minimum Additional Hourly Rate is 1";
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "minimumHours" && parseInt(value) < 1) {
      setIsMinimumHourError(true);
    } else {
      setIsMinimumHourError(false);
    }
    dispatch(
      updateWorkOrderForm({
        step: "payment",
        data: {
          pay: {
            [name]: value,
          },
        },
      }),
    );

    if (name === "minimumHours" && value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "minimumHours",
          data: false,
        }),
      );
    }
    if (name === "minimumHours" && (!value || parseInt(value) === 0)) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "minimumHours",
          data: true,
        }),
      );
    }

    if (name === "hourlyRate" && value >= 20) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "hourlyRate",
          data: false,
        }),
      );
    }
    if (name === "hourlyRate" && !value) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "hourlyRateErrorMsg",
          data: "Hourly Rate is required",
        }),
      );
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "hourlyRate",
          data: true,
        }),
      );
    }
    if (name === "additionalHourlyRate" && payment.enableClientAdditionalHourRate) {
      // One dispatch for every outcome — the old pair of conditions both missed
      // an emptied field, which left the previous message stranded in red.
      const isValidRate = value !== "" && parseFloat(value) > 0;
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "additionalHourlyRateError",
          data: isValidRate ? "" : additionalHourlyRateError(value),
        }),
      );
    }
  };

  return (
    <div className="grid grid-cols-2 gap-y-4 mt-3">
      <div className="lg:mr-10 lg:col-span-1 col-span-2">
        <BaseTextField
          disabled={disabled}
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.hourlyRate}
          errorMsg={workOrderFormValidation.hourlyRateErrorMsg}
          fullWidth
          fullHeight={false}
          id="hourlyRate"
          name="hourlyRate"
          label="Hourly Rate"
          requirement="required"
          labelShrink={false}
          value={payment.hourlyRate}
          onChange={handleChange}
          isDefaultField={false}
          type="float"
        />
      </div>
      <div className="lg:col-span-1 col-span-2">
        <BaseTextField
          disabled={disabled || !payment.enableClientAdditionalHourRate}
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.additionalHourlyRateError}
          errorMsg={workOrderFormValidation.additionalHourlyRateError}
          fullWidth
          fullHeight={false}
          id="additionalHourlyRate"
          name="additionalHourlyRate"
          label={
            <div className="flex">
              <p className="mr-2 font-normal">
                Additional Hourly Rate
                {/* Only demanded once the checkbox opts the rate in. */}
                {payment.enableClientAdditionalHourRate && (
                  <span className="text-red-500 ml-0.5" aria-hidden="true">
                    *
                  </span>
                )}
              </p>
              <BaseCheckbox
                id="enableClientAdditionalHourRate"
                name="enableClientAdditionalHourRate"
                disabled={disabled}
                checked={payment.enableClientAdditionalHourRate || false}
                onChange={() => {
                  if (payment.enableClientAdditionalHourRate) {
                    dispatch(
                      updateWorkOrderFormValidation({
                        fieldName: "additionalHourlyRateError",
                        data: "",
                      }),
                    );
                  }
                  if (
                    !payment.enableClientAdditionalHourRate &&
                    payment.additionalHourlyRate <= 0
                  ) {
                    dispatch(
                      updateWorkOrderFormValidation({
                        fieldName: "additionalHourlyRateError",
                        data: additionalHourlyRateError(payment.additionalHourlyRate),
                      }),
                    );
                  }
                  dispatch(
                    updateWorkOrderForm({
                      step: "payment",
                      data: {
                        pay: {
                          ...payment,
                          enableClientAdditionalHourRate: !payment.enableClientAdditionalHourRate,
                        },
                      },
                    }),
                  );
                }}
              />
            </div>
          }
          optional={false}
          labelShrink={false}
          value={payment.additionalHourlyRate}
          onChange={handleChange}
          isDefaultField={false}
          type="float"
        />
      </div>
      <div className="lg:col-span-1 col-span-2 lg:mr-10">
        <BaseTextField
          disabled={disabled}
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.minimumHours}
          errorMsg="Minimum 1 hour is required"
          fullWidth
          fullHeight={false}
          id="minimumHours"
          name="minimumHours"
          label="Minimum Hours"
          requirement="required"
          labelShrink={false}
          required={true}
          value={payment.minimumHours}
          onChange={handleChange}
          isDefaultField={false}
          /* onBlur={() => {
            dispatch(
              updateWorkOrderFormValidation({
                fieldName: "minimumHours",
                data: false,
              })
            );
          }} */
          type="float"
          bottomRightMsg="Minimum Payable Hours"
        />
      </div>
    </div>
  );
};

export default HourlyPay;
